import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import {
    Badge,
    Box,
    Button,
    Group,
    Modal,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Title,
    Tooltip,
    ActionIcon,
} from '@mantine/core'
import { DataTable } from 'mantine-datatable'
import {
    IconArrowRight,
    IconReportAnalytics,
    IconSend,
    IconSettings,
} from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { DatasetType } from '@/lib/domain'
import type { AudienceConfiguration, Dataset } from '@/lib/domain'
import { datasetIdToName } from '@/lib/datasetNames'
import { EmptyState } from '@/components/EmptyState'
import { AttentionBanner } from '@/components/AttentionBanner'
import { ListSkeleton } from '@/components/ListSkeleton'
import { StatPill } from '@/components/StatPill'
import {
    destinationType,
    DESTINATION_TYPE_LABEL,
} from '@/lib/marketplaceDestinations'
import { matchColor } from '@/lib/matchColor'
import { RUN_STATUS_META } from '@/features/segment/labels'
import { estimateMatchPct, segmentKey } from '@/features/segment/api'
import { useSegmentStubs } from '@/features/segment/queries'
import {
    SegmentDetail,
    type SegmentStage,
} from '@/features/segment/components/SegmentDetail'
import { DistributionConfigModal } from '@/features/segment/components/DistributionConfigModal'
import { formatCompactNumber } from '@/lib/formatNumber'
import {
    useDistributionsInventory,
    type DistributionRow,
} from '@/hooks/useDistributionsInventory'

// Status shown for a row's latest run (or "Scheduled" before its first run).
const runStatusMeta = (
    row: DistributionRow
): { label: string; color: string } =>
    row.latestRun
        ? RUN_STATUS_META[row.latestRun.status]
        : { label: 'Scheduled', color: 'gray' }

const SHAPE_LABEL: Record<DatasetType, string> = {
    [DatasetType.TRANSACTION]: 'Transaction',
    [DatasetType.USER_ATTRIBUTE]: 'Attribute',
    [DatasetType.CROSSED]: 'Crossed',
}

type StatusFilter = 'all' | 'attention' | 'live' | 'scheduled'

function parseStatus(value: string | null): StatusFilter {
    return value === 'attention' || value === 'live' || value === 'scheduled'
        ? value
        : 'all'
}

type DetailState = {
    config: AudienceConfiguration
    threadId: string
    stage: SegmentStage
    datasets: Dataset[]
} | null

// "Did it land" — records pushed + matched at the destination, the trust metric
// (kept identical to SegmentDetail / Audiences). data-delivery has no match
// step, so success = rows delivered; not-yet-landed runs show nothing.
function RecordsCell({ row }: { row: DistributionRow }) {
    const d = row.latestRun
    const landed = d?.status === 'succeeded' || d?.status === 'partial'
    if (!d || !landed) return <Text c='dimmed'>—</Text>
    if (d.recordsMatched === null && d.recordsPushed != null) {
        return (
            <Stack gap={0}>
                <Text size='sm'>Delivered</Text>
                <Text size='xs' c='dimmed'>
                    {formatCompactNumber(d.recordsPushed)} rows
                </Text>
            </Stack>
        )
    }
    if (d.recordsMatched != null && d.recordsPushed) {
        const pct = Math.round((d.recordsMatched / d.recordsPushed) * 100)
        return (
            <Stack gap={0}>
                <Group gap={6}>
                    <Text size='sm' c={matchColor(pct)} fw={600}>
                        {pct}% match
                    </Text>
                </Group>
                <Text size='xs' c='dimmed'>
                    {formatCompactNumber(d.recordsMatched)} of{' '}
                    {formatCompactNumber(d.recordsPushed)}
                </Text>
            </Stack>
        )
    }
    return <Text c='dimmed'>—</Text>
}

// Freshness: when it last ran + when it next refreshes. v1 mock cadence — live
// runs refresh daily; flag the refresh overdue if that next slot has passed.
function FreshnessCell({ row, now }: { row: DistributionRow; now: number }) {
    const d = row.latestRun
    if (!d?.runAt) return <Text c='dimmed'>—</Text>
    const ranAt = new Date(d.runAt)
    const landed = d.status === 'succeeded' || d.status === 'partial'
    const nextRun = landed ? new Date(ranAt.getTime() + 24 * 3600 * 1000) : null
    const overdue = nextRun != null && nextRun.getTime() < now
    return (
        <Stack gap={0}>
            <Text size='sm'>
                ran {formatDistanceToNow(ranAt, { addSuffix: true })}
            </Text>
            {nextRun &&
                (overdue ? (
                    <Text size='xs' c='error' fw={600}>
                        refresh overdue
                    </Text>
                ) : (
                    <Text size='xs' c='dimmed'>
                        refreshes daily · next{' '}
                        {formatDistanceToNow(nextRun, { addSuffix: true })}
                    </Text>
                ))}
        </Stack>
    )
}

export function DistributionsPage() {
    const { rows, destinations, summary, isLoading } =
        useDistributionsInventory()
    const { addDemoRunHistory } = useSegmentStubs()

    // All three filters are URL-driven (?status=, ?audience=, ?dest=) so the
    // dashboard pipeline pills, the stats strip below, and an audience's "Not
    // distributed" link can deep-link straight to a pre-filtered view.
    const [searchParams, setSearchParams] = useSearchParams()
    const setParam = (key: string, value: string) =>
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev)
                if (value === 'all') next.delete(key)
                else next.set(key, value)
                return next
            },
            { replace: true }
        )
    const status = parseStatus(searchParams.get('status'))
    const setStatus = (value: StatusFilter) => setParam('status', value)
    const destId = searchParams.get('dest') ?? 'all'
    const setDestId = (value: string) => setParam('dest', value)
    const audience = searchParams.get('audience') ?? 'all'
    const setAudience = (value: string) => setParam('audience', value)
    const [detail, setDetail] = useState<DetailState>(null)
    // The row whose distribution config is being finished/edited, if any.
    const [configuring, setConfiguring] = useState<DistributionRow | null>(null)
    // Snapshot "now" once per render so the table's relative times are stable.
    const now = Date.now()

    // Runs whose destination config is unfinished (or went stale from an upstream
    // change). Distinct destinations, for the just-in-time setup nudge.
    const configIssues = useMemo(
        () => rows.filter((r) => r.configState.status === 'needs-config'),
        [rows]
    )
    const configIssueDestinations = useMemo(
        () =>
            [
                ...new Set(
                    configIssues.map((r) => r.destination?.platform ?? '')
                ),
            ].filter(Boolean),
        [configIssues]
    )

    const audienceOptions = useMemo(() => {
        const names = [...new Set(rows.map((r) => r.audienceName))].sort()
        return [
            { value: 'all', label: 'All audiences' },
            ...names.map((n) => ({ value: n, label: n })),
        ]
    }, [rows])

    const destinationOptions = useMemo(
        () => [
            { value: 'all', label: 'All destinations' },
            ...destinations.map((d) => ({ value: d.id, label: d.platform })),
        ],
        [destinations]
    )

    const filtered = useMemo(() => {
        return rows.filter((r) => {
            const s = r.latestRun?.status
            const needsConfig = r.configState.status === 'needs-config'
            const statusOk =
                status === 'all'
                    ? true
                    : status === 'attention'
                      ? s === 'failed' || needsConfig
                      : status === 'live'
                        ? // Attention wins over a successful run, so a
                          // succeeded-but-needs-config row is not "live".
                          (s === 'succeeded' || s === 'partial') && !needsConfig
                        : !s || s === 'queued' || s === 'running'
            const destOk =
                destId === 'all' || r.distribution.destinationId === destId
            const audOk = audience === 'all' || r.audienceName === audience
            return statusOk && destOk && audOk
        })
    }, [rows, status, destId, audience])

    return (
        <Stack gap='lg' h='100%'>
            <Group
                justify='space-between'
                align='flex-start'
                style={{ flexShrink: 0 }}
            >
                <div>
                    <Title order={2}>Distributions</Title>
                    <Text c='dimmed'>
                        Did your audiences reach their destinations — are they
                        live, current, and did anything fail?
                    </Text>
                </div>
                <Button
                    component={Link}
                    to='/audiences'
                    leftSection={<IconSend size={16} />}
                >
                    Distribute an audience
                </Button>
            </Group>

            {isLoading ? (
                <ListSkeleton count={8} height={48} />
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={<IconSend size={28} />}
                    title='Nothing distributed yet'
                    description='Distribute an audience and every push to a destination will show up here with its status and match.'
                />
            ) : (
                <>
                    {/* Fixed header region — never compresses */}
                    <Stack gap='lg' style={{ flexShrink: 0 }}>
                        {/* Problems first */}
                        {summary.attention > 0 && (
                            <AttentionBanner
                                title={`${summary.attention} distribution${summary.attention === 1 ? '' : 's'} need your attention`}
                            >
                                Failed pushes and runs needing setup are listed
                                first below, each with a next step.
                            </AttentionBanner>
                        )}

                        {configIssues.length > 0 && (
                            <AttentionBanner
                                icon={<IconSettings size={18} />}
                                title={`${configIssues.length} run${configIssues.length === 1 ? '' : 's'} need destination setup finished`}
                            >
                                Configuration is incomplete (or changed
                                upstream) for{' '}
                                {configIssueDestinations.join(', ')}. These runs
                                can&apos;t deliver until setup is finished — use
                                <strong> Finish setup</strong> on a run below.
                            </AttentionBanner>
                        )}

                        {/* Summary readout — the same number + label strip as
                            the dashboard pipeline. Clicking a stat applies that
                            status filter in place (the SegmentedControl below
                            stays in sync via the shared ?status= param). */}
                        <Group gap='xl' wrap='wrap'>
                            {(
                                [
                                    {
                                        label: 'Need attention',
                                        value: summary.attention,
                                        color: 'warning',
                                        status: 'attention',
                                    },
                                    {
                                        label: 'Live',
                                        value: summary.live,
                                        color: 'success',
                                        status: 'live',
                                    },
                                    {
                                        label: 'Scheduled',
                                        value: summary.scheduled,
                                        color: 'info',
                                        status: 'scheduled',
                                    },
                                    {
                                        label: 'Total runs',
                                        value: summary.total,
                                        color: 'gray',
                                        status: 'all',
                                    },
                                ] as const
                            ).map((s) => (
                                <StatPill
                                    key={s.status}
                                    value={s.value}
                                    label={s.label}
                                    color={s.color}
                                    onClick={() => setStatus(s.status)}
                                />
                            ))}
                        </Group>

                        <Group gap='sm' justify='space-between' wrap='wrap'>
                            <SegmentedControl
                                value={status}
                                onChange={(v) => setStatus(v as StatusFilter)}
                                data={[
                                    { value: 'all', label: 'All' },
                                    {
                                        value: 'attention',
                                        label: 'Needs attention',
                                    },
                                    { value: 'live', label: 'Live' },
                                    { value: 'scheduled', label: 'Scheduled' },
                                ]}
                            />
                            <Group gap='sm'>
                                <Select
                                    aria-label='Filter by audience'
                                    data={audienceOptions}
                                    value={audience}
                                    onChange={(v) => setAudience(v ?? 'all')}
                                    w={220}
                                    searchable
                                />
                                <Select
                                    aria-label='Filter by destination'
                                    data={destinationOptions}
                                    value={destId}
                                    onChange={(v) => setDestId(v ?? 'all')}
                                    w={190}
                                />
                            </Group>
                        </Group>
                    </Stack>

                    {/* Scroll region — the table absorbs remaining height, with
                        a bottom gap so it doesn't sit flush on the window edge. */}
                    <Box pb='md' style={{ flex: 1, minHeight: 0 }}>
                        <DataTable
                            height='100%'
                            withTableBorder
                            borderRadius='md'
                            // Sit on the elevated surface + subtle shadow so the
                            // table matches the app's cards (see /design Surfaces).
                            backgroundColor='var(--mantine-color-default)'
                            style={{ boxShadow: 'var(--mantine-shadow-xs)' }}
                            // Match the Dashboard / Threads rows: a uniform
                            // surface (no stripes) that hovers to the shared
                            // default-hover, not mantine-datatable's own color.
                            highlightOnHover
                            highlightOnHoverColor='var(--mantine-color-default-hover)'
                            minHeight={160}
                            idAccessor='id'
                            records={filtered}
                            noRecordsText='No distributions match these filters'
                            onRowClick={({ record }) =>
                                setDetail({
                                    config: record.config,
                                    threadId: record.threadId,
                                    stage: 'distribute',
                                    datasets: record.datasets,
                                })
                            }
                            onCellClick={({ event, column }) => {
                                if (column.accessor === 'actions') {
                                    event.stopPropagation()
                                }
                            }}
                            columns={[
                                {
                                    accessor: 'audience',
                                    title: 'Audience / segment',
                                    render: (row) => (
                                        <Stack gap={2}>
                                            <Text size='sm' fw={500}>
                                                {row.audienceName}
                                            </Text>
                                            <Group gap={6}>
                                                <Text size='xs' c='dimmed'>
                                                    {row.segmentName}
                                                </Text>
                                                <Badge
                                                    size='xs'
                                                    variant='light'
                                                    color='gray'
                                                >
                                                    {
                                                        SHAPE_LABEL[
                                                            row.segmentType
                                                        ]
                                                    }
                                                </Badge>
                                            </Group>
                                        </Stack>
                                    ),
                                },
                                {
                                    accessor: 'destination',
                                    title: 'Destination',
                                    render: (row) => (
                                        <Stack gap={2}>
                                            <Text size='sm'>
                                                {row.destination?.platform ??
                                                    row.distribution
                                                        .destinationId}
                                            </Text>
                                            {row.destination && (
                                                <Text size='xs' c='dimmed'>
                                                    {
                                                        DESTINATION_TYPE_LABEL[
                                                            destinationType(
                                                                row.destination
                                                                    .platform
                                                            )
                                                        ]
                                                    }
                                                </Text>
                                            )}
                                            {row.configState.status ===
                                                'needs-config' && (
                                                <Tooltip
                                                    multiline
                                                    w={240}
                                                    label={`Needs setup: ${row.configState.missing.join(', ')}`}
                                                >
                                                    <Badge
                                                        size='xs'
                                                        variant='light'
                                                        color='warning'
                                                        leftSection={
                                                            <IconSettings
                                                                size={11}
                                                            />
                                                        }
                                                        style={{
                                                            width: 'fit-content',
                                                        }}
                                                    >
                                                        Needs config
                                                    </Badge>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    ),
                                },
                                {
                                    accessor: 'status',
                                    title: 'Status',
                                    render: (row) => {
                                        const m = runStatusMeta(row)
                                        // Hidden demo affordance: double-clicking
                                        // the "Scheduled" chip (no history yet)
                                        // fabricates a realistic run history.
                                        const canSeed = row.runs.length === 0
                                        return (
                                            <Badge
                                                variant='light'
                                                color={m.color}
                                                // Swallow single clicks so the
                                                // double-click trigger doesn't
                                                // also open the row detail.
                                                onClick={
                                                    canSeed
                                                        ? (e) =>
                                                              e.stopPropagation()
                                                        : undefined
                                                }
                                                onDoubleClick={
                                                    canSeed
                                                        ? (e) => {
                                                              e.stopPropagation()
                                                              addDemoRunHistory(
                                                                  {
                                                                      distributionId:
                                                                          row
                                                                              .distribution
                                                                              .id,
                                                                      recordsBase:
                                                                          row
                                                                              .config
                                                                              .audience_size ??
                                                                          0,
                                                                      matchPct:
                                                                          estimateMatchPct(
                                                                              segmentKey(
                                                                                  row.config
                                                                              )
                                                                          ),
                                                                      dataDelivery:
                                                                          destinationType(
                                                                              row
                                                                                  .destination
                                                                                  ?.platform ??
                                                                                  ''
                                                                          ) ===
                                                                          'data-delivery',
                                                                      schedule:
                                                                          row
                                                                              .distribution
                                                                              .schedule,
                                                                  }
                                                              )
                                                          }
                                                        : undefined
                                                }
                                            >
                                                {m.label}
                                            </Badge>
                                        )
                                    },
                                },
                                {
                                    accessor: 'records',
                                    title: 'Records & match',
                                    render: (row) => <RecordsCell row={row} />,
                                },
                                {
                                    accessor: 'lastRun',
                                    title: 'Last run / refresh',
                                    render: (row) => (
                                        <FreshnessCell row={row} now={now} />
                                    ),
                                },
                                {
                                    accessor: 'nextStep',
                                    title: 'Next step',
                                    render: (row) => {
                                        const step =
                                            row.configState.status ===
                                            'needs-config'
                                                ? `Finish setup: ${row.configState.missing.join(', ')}`
                                                : row.latestRun?.nextStep
                                        return step ? (
                                            <Text
                                                size='xs'
                                                c='warning'
                                                maw={240}
                                            >
                                                {step}
                                            </Text>
                                        ) : (
                                            <Text c='dimmed'>—</Text>
                                        )
                                    },
                                },
                                {
                                    accessor: 'actions',
                                    title: '',
                                    textAlign: 'right',
                                    render: (row) => (
                                        <Group
                                            gap={4}
                                            justify='flex-end'
                                            wrap='nowrap'
                                        >
                                            {row.configState.status ===
                                                'needs-config' && (
                                                <Tooltip label='Finish setup'>
                                                    <ActionIcon
                                                        variant='light'
                                                        color='warning'
                                                        aria-label='Finish setup'
                                                        onClick={() =>
                                                            setConfiguring(row)
                                                        }
                                                    >
                                                        <IconSettings
                                                            size={18}
                                                        />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                            <Tooltip label='Run detail'>
                                                <ActionIcon
                                                    variant='subtle'
                                                    color='gray'
                                                    aria-label='Run detail'
                                                    onClick={() =>
                                                        setDetail({
                                                            config: row.config,
                                                            threadId:
                                                                row.threadId,
                                                            stage: 'distribute',
                                                            datasets:
                                                                row.datasets,
                                                        })
                                                    }
                                                >
                                                    <IconReportAnalytics
                                                        size={18}
                                                    />
                                                </ActionIcon>
                                            </Tooltip>
                                            <Tooltip label='Open thread'>
                                                <ActionIcon
                                                    variant='subtle'
                                                    color='gray'
                                                    aria-label='Open thread'
                                                    component={Link}
                                                    to={`/thread/${row.threadId}`}
                                                >
                                                    <IconArrowRight size={18} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Group>
                                    ),
                                },
                            ]}
                        />
                    </Box>
                </>
            )}

            <Modal
                opened={detail !== null}
                onClose={() => setDetail(null)}
                size='90%'
                title={
                    <Text size='xl' fw='bold'>
                        <IconReportAnalytics
                            style={{ verticalAlign: 'text-bottom' }}
                        />{' '}
                        {detail?.config.name || 'Run detail'}
                    </Text>
                }
                overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            >
                {detail && (
                    <SegmentDetail
                        config={detail.config}
                        threadId={detail.threadId}
                        names={datasetIdToName}
                        datasets={detail.datasets}
                        initialStage={detail.stage}
                        onClose={() => setDetail(null)}
                    />
                )}
            </Modal>

            {configuring && (
                <DistributionConfigModal
                    key={configuring.distribution.id}
                    distribution={configuring.distribution}
                    destination={configuring.destination}
                    opened
                    onClose={() => setConfiguring(null)}
                />
            )}
        </Stack>
    )
}
